<!-- eslint-disable import/no-unresolved -->
<script setup lang="ts">
import { storeToRefs } from 'pinia';

import FoxAndClover from '@clovyr/bed/assets/images/fox-and-clover.png';
import { GridItem, GridSection } from '@clovyr/bed/components/blocks';
import Markdown from '@clovyr/bed/components/blocks/Markdown.vue';
import { ComputeProviderName } from '@clovyr/pollen/types/Host';

import AppConfiguration from '@/components/forms/AppConfiguration.vue';
import HostSelector from '@/components/forms/HostSelector.vue';
import AppLauncherHero from '@/components/tiers/AppLauncherHero.vue';
import { useLauncherStore } from '@/stores/launcher_store';

import AddonsBackup from './AddonsBackup.vue';
import BYODomain from './BYODomain.vue';

const { basicSettings, advancedSettings, metadata } = storeToRefs(useLauncherStore());
const launcherStore = useLauncherStore();
const { selectedHost } = storeToRefs(launcherStore);

const onlyOptionalSettings = 'Configuration is complete.';

const appConfigHeading = computed(() => {
  if (basicSettings.value?.length > 0) {
    // TODO: skip counting for now as we weren't looking at nested settings
    return 'These items need your input:';
  }
  if (advancedSettings.value?.length) {
    return onlyOptionalSettings;
  }
  return '';
});

const isWNextSelected = computed(() => selectedHost.value?.id === ComputeProviderName.WNext);
</script>

<template>
  <div class="launch-setup">
    <div class="launch-setup__main">
      <AppLauncherHero
        :app-logo="metadata"
        :app-title="metadata.name"
        :right-image-src="FoxAndClover"
        :heading-text="`Set up ${metadata.name}`"
      />

      <GridSection :spacing="1" wrap>
        <GridItem :md="6">
          <div class="setup-controls-heading launch-heading--secondary">Hosting On:</div>
          <div class="section-highlighted background-gradient--base selector">
            <HostSelector />
          </div>
        </GridItem>
        <!-- App config -->
        <GridItem :md="6" class="config">
          <div class="setup-controls__left grid__col" v-if="appConfigHeading">
            <div class="setup-controls-heading launch-heading--secondary">App Configuration:</div>
            <div
              v-if="appConfigHeading !== onlyOptionalSettings"
              class="setup-controls-heading launch-heading--secondary"
            >
              <template>
                {{ appConfigHeading }}
              </template>
            </div>

            <div class="section-highlighted background-gradient--base">
              <div
                v-if="appConfigHeading === onlyOptionalSettings"
                class="setup-controls-heading launch-heading--secondary"
              >
                {{ appConfigHeading }}
              </div>
              <AppConfiguration
                untoggled-button-text="show optional settings"
                toggled-button-text="hide optional settings"
                @heading="(h) => (appConfigHeading = h)"
                class="options-block"
              />
            </div>
          </div>
          <div v-else class="section-highlighted background-gradient--base alt-appconfig">
            <markdown :content="metadata.description" />
          </div>

          <BYODomain :bannerStatus="'info'" class="domain" />
          <AddonsBackup v-if="!isWNextSelected" />
        </GridItem>
      </GridSection>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-utils';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/fonts';
@use 'styles/core/typography/type';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.launch-setup {
  &__main {
    margin-bottom: 0;

    .alt-appconfig {
      margin-top: grid-vars.space(5.5);
      @include breakpoints.media-breakpoint-down(xs) {
        margin-top: grid-vars.space(1);
      }
    }
    .selector {
      padding: grid-vars.space(3);
    }

    :deep(.hero-masthead) {
      @include breakpoints.media-breakpoint-up(xl) {
        margin-bottom: 0;
      }

      .hero-masthead__right {
        @include breakpoints.media-breakpoint-up(xl) {
          margin-top: 0;
        }
      }
    }

    .config {
      margin-bottom: 3rem;
      padding-top: 3rem;
      @include breakpoints.media-breakpoint-up(sm) {
        margin-bottom: 0;
        padding-top: 0;
      }

      :deep(.content-wrapper) {
        font-size: 14px;
      }
    }

    .addon-backup {
      padding-bottom: 2rem;

      @include breakpoints.media-breakpoint-up(sm) {
        padding-bottom: 0;
        margin-top: 3rem;
      }

      :deep(.setup) {
        margin-top: 2rem;

        @include breakpoints.media-breakpoint-up(md) {
          margin-top: 0;
        }
      }
    }
  }
}

.launch-setup,
.review-setup {
  .setup-controls {
    display: flex;

    &__left {
      flex-basis: grid-utils.col-width(6, modal);
    }

    &__right {
      flex-basis: grid-utils.col-width(4, modal);
    }
  }

  .setup-controls-heading {
    @include type.text--h4;

    margin: 0 0 0.5rem 1rem;
    color: colors.color(white);
  }

  .setup-grid-banner {
    @include fonts.font-size(15);

    display: flex;
    align-items: center;
    padding: grid-vars.space(2) grid-vars.space(5);

    @include breakpoints.media-breakpoint-up(md) {
      margin-top: 2rem;
    }
  }
}
</style>
